import React from "react";

/**
 * RC10l · Driver availability UI badges.
 *
 * Thin renderers over the already-correct RC10l backend eligibility
 * payload. No local date math, no local availability logic. The
 * backend `DriverEligibilityService` is the single source of truth.
 *
 * Four badges per the RC10l dispatch:
 *   - Available              (green)  — rendered implicitly: no badge
 *   - Unavailable: {s}–{e}   (red)
 *   - Assigned to another Tour: {name} (amber)
 *   - Coverage needed        (amber)
 *
 * The "Available" case is intentionally silent — we do not clutter
 * the row with a redundant "Available" chip when there is nothing to
 * warn about. Drivers with no explicit record are available-by-
 * default and therefore render with no RC10l badge.
 */

// ---------- helpers ----------

// En-dash per dispatch wording (U+2013). Kept as a literal here so
// the exact character shows up in grep assertions during review.
const EN_DASH = "–";

// Finds the first block_reason with the given code. Returns null if
// none. Safe against missing / null eligibility payloads.
function findReason(eligibility, code) {
  const list = (eligibility && eligibility.block_reasons) || [];
  for (const r of list) {
    if (r && r.code === code) return r;
  }
  return null;
}

function fmtRange(start, end) {
  // Keep ISO dates as-is for consistency with Tour Detail
  // ("{r.start_date} → {r.end_date}") and Schedule. Backend sends
  // calendar dates (no times), already in operator-visible format.
  if (!start && !end) return "";
  if (!end || start === end) return String(start || "");
  return `${start}${EN_DASH}${end}`;
}

// ---------- badges ----------

export function UnavailabilityDateRangeBadge({ eligibility, testid }) {
  const reason = findReason(eligibility, "UNAVAILABLE");
  if (!reason) return null;
  const ctx = reason.context || {};
  const range = fmtRange(ctx.start_date, ctx.end_date);
  return (
    <span
      data-testid={testid || "rc10l-badge-unavailable"}
      title={reason.message || "Driver has an explicit UNAVAILABLE window overlapping the requested range"}
      className="inline-flex items-center border rounded-sm px-1.5 py-0.5 text-[10.5px] uppercase tracking-widest font-medium bg-critical-red/10 text-critical-red border-critical-red/40 whitespace-nowrap"
    >
      Unavailable: {range}
    </span>
  );
}

export function AssignmentConflictBadge({ eligibility, testid }) {
  const reason = findReason(eligibility, "ASSIGNMENT_CONFLICT");
  if (!reason) return null;
  const ctx = reason.context || {};
  const tourLabel =
    ctx.conflicting_tour_display_name
      || ctx.conflicting_tour_number
      || (ctx.conflicting_tour_id ? String(ctx.conflicting_tour_id).slice(0, 8) : "another Tour");
  return (
    <span
      data-testid={testid || "rc10l-badge-assignment-conflict"}
      title={reason.message || "Driver is committed to a conflicting Tour"}
      className="inline-flex items-center border rounded-sm px-1.5 py-0.5 text-[10.5px] uppercase tracking-widest font-medium bg-amber-hold/15 text-amber-hold border-amber-hold/50 whitespace-nowrap"
    >
      Assigned to another Tour: {tourLabel}
    </span>
  );
}

export function CoverageNeededBadge({ testid }) {
  return (
    <span
      data-testid={testid || "rc10l-badge-coverage-needed"}
      className="inline-flex items-center border rounded-sm px-1.5 py-0.5 text-[10.5px] uppercase tracking-widest font-medium bg-amber-hold/15 text-amber-hold border-amber-hold/50 whitespace-nowrap"
    >
      Coverage needed
    </span>
  );
}

/**
 * RC10l composite — renders all applicable RC10l badges for a given
 * eligibility payload. Returns null when the driver is available-by-
 * default (no record / confirmed_available).
 */
export function RC10lEligibilityBadges({ eligibility }) {
  if (!eligibility) return null;
  const hasUnavail = !!findReason(eligibility, "UNAVAILABLE");
  const hasConflict = !!findReason(eligibility, "ASSIGNMENT_CONFLICT");
  if (!hasUnavail && !hasConflict) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid="rc10l-badges">
      {hasUnavail && <UnavailabilityDateRangeBadge eligibility={eligibility} />}
      {hasConflict && <AssignmentConflictBadge eligibility={eligibility} />}
    </span>
  );
}
